import { useEffect, useState } from "react";
import poweredByDark from "@/assets/powered-by-dark.png";
import poweredByLight from "@/assets/powered-by-light.png";
import { getAccountRequest, initPaymentFromAccountRequest, postScaComplete } from "./api/accounts";
import { BarLoader } from "react-spinners";
import { toast } from "sonner";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getPayment } from "./api/payment";
import type { CSSProperties } from "react";

const override: CSSProperties = {
  display: "block",
  margin: "0 auto",
  borderColor: "red",
};

function ScaComplete() {
  const [scaPollingInterval, setScaPollingInterval] = useState<number>(0);
  const [paymentPollingInterval, setPaymentPollingInterval] =
    useState<number>(0);
  const [scaProcessComplete, setScaProcessComplete] = useState(false);

  // Set a timeout to stop the process if it takes too long
  const timeout = 60000; // timeout this process in 60 seconds
  const processTimeout = setTimeout(() => {
    if (!scaProcessComplete) {
      toast.error("Villa kom upp við auðkenningu.");
      setScaProcessComplete(true); // stop polling
      setTimeout(() => {
        if (initPayment?.data.paymentId === "") {
          window.location.replace("/"); // no payment to process, go to home
        } else {
          setPaymentPollingInterval(1000); // start payment polling
        }
      }, 3000); // give the user some time to read the toast message
    }
  }, timeout);

  // On component mount, parse URL parameters and initiate SCA complete process
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get("code");
  const state = urlParams.get("state"); // can be used for both the account request ID and payment IDs
  useEffect(() => {
    // do SCA complete post back
    if (state && code) {
      // post the the code and state to the API to indicate that the user has completed the SCA
      // state must contain the account request ID
      completeScaProcess.mutate({ id: state, codeChallange: code });
    }

    // cleanup function to clear the timeout and reset state, maybe not needed
    return () => {
      clearTimeout(processTimeout);
      setScaProcessComplete(false);
    };
  }, []);

  // Mutation to complete the SCA process
  const completeScaProcess = useMutation({
    mutationFn: async (variables: { id: string; codeChallange: string }) => {
      return await postScaComplete(variables.id, variables.codeChallange);
    },
    onError: (error) => {
      console.error("Error completing SCA process:", error);
      toast.error("Villa kom upp við auðkenningu.");
      setScaProcessComplete(true); // stop polling
    },
    onSuccess: (_) => {
      setScaPollingInterval(1000); // start polling for account request status
    },
  });

  // Mutation to initiate payment from account request
  const initPayment = useMutation({
    mutationFn: async (variables: { accountReqId: string }) => {
      return await initPaymentFromAccountRequest(variables.accountReqId);
    },
    onError: (error) => {
      console.error("Error initiating payment from account request:", error);
      toast.error("Villa kom upp við auðkenningu.");
    },
    onSuccess: (data) => {
      if (data && data.paymentId === "") {
        setTimeout(() => {
          window.location.replace("/"); // no payment to process, go to home
        }, 3000); // give the user some time to read the toast message
      } else {
        setPaymentPollingInterval(1000); // start payment polling
      }
    },
  });

  // Poll for account request status if we have an account request ID and the SCA process is not complete
  const { data: accountRequestData, error: accountRequestError } = useQuery({
    queryKey: ["accountRequest", { state }],
    queryFn: () => getAccountRequest(state!),
    refetchInterval: scaPollingInterval,
    enabled: completeScaProcess.isSuccess || completeScaProcess.isError,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        console.error("Error fetching account request:", error);
        return false;
      }
      return true;
    },
  });


  // Handle account request data and errors
  useEffect(() => {
    if (accountRequestError) {
      console.error("Error fetching account request:", accountRequestError);
      toast.error("Villa kom upp við auðkenningu.");
      setScaProcessComplete(true); // stop account request polling
      return;
    }
    if (accountRequestData) {
      switch (accountRequestData.status) {
        // @ts-ignore
        case "SUCCESS":
          toast.success("Reikningur tengdur");
        // NOTE: fallthrough to ERROR case to handle redirect or payment initiation
        // eslint-disable-next-line no-fallthrough 
        case "ERROR":
          // Payment Wallet page uses this whitelisted page for completing the consent process for ISB accounts
          if (accountRequestData.postScaRedirectUrl) {
            window.location.replace(accountRequestData.postScaRedirectUrl); // if we have a post SCA redirect URL, go there and let it handle the next steps
          } else {
            completeScaProcess.reset();
            setScaPollingInterval(0); // stop account request polling
            initPayment.mutate({ accountReqId: state! });
          }
          return;
        default:
          return undefined; // continue account request polling
      }
    }
  }, [accountRequestData, accountRequestError]);

  // Poll for payment status if we have a payment ID and the SCA process is complete
  const { data: paymentData, error: paymentError } = useQuery({
    queryKey: ["payment", { paymentId: initPayment.data?.paymentId }],
    queryFn: () => getPayment(initPayment.data?.paymentId),
    refetchInterval: paymentPollingInterval,
    enabled: initPayment.isSuccess && !!initPayment.data?.paymentId,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        console.error("Error fetching payment:", error);
        return false;
      }
      return true;
    },
  });

  // Handle payment data and errors
  useEffect(() => {
    if (paymentError) {
      console.error("Error fetching payment:", paymentError);
      toast.error("Villa kom upp við auðkenningu.");
      setPaymentPollingInterval(0); // stop payment polling
      return;
    }

    if (paymentData) {
      switch (paymentData.status) {
        case "PENDING":
        case "SCA_REQUIRED":
        case "SCA_COMPLETE":
        case "SUCCESS":
        case "ERROR":
        case "CANCELLED":
        case "REJECTED":
          setPaymentPollingInterval(0); // stop payment polling
          const redirectHost = window.location.origin.replace("light", "payment")
          window.location.href = `${redirectHost}/?id=${paymentData.id}`;
          return;
        default:
          return; // continue payment polling
      }
    }
  }, [paymentData, paymentError]);

  return (
    <div className="flex flex-col items-center p-8">
      <a href="https://blikk.tech/" className="underline" target="_blank">
        <img
          src={poweredByLight}
          alt="Powered by Blikk"
          className="block dark:hidden h-12 w-auto"
        />
        <img
          src={poweredByDark}
          alt="Powered by Blikk"
          className="hidden dark:block h-12 w-auto"
        />
      </a>
      <p className="text-center text-sm max-w-md py-6">
        Við erum að klára, dokaðu við...
      </p>
      <BarLoader
        color={"#8CEE7B"}
        loading={true}
        cssOverride={override}
        aria-label="Loading Spinner"
        data-testid="loader"
        width={210}
      />
    </div>
  );
}

export default ScaComplete;
